import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { useTitoliPagina } from "@/components/TitoliPagina";
import { FilterBar, inputCls, useFiltersMeta } from "@/components/Filters";
import { dateIt, eurShort, num } from "@/lib/format";
import DealerForm from "@/components/DealerForm";
import { ScorporoDealer } from "@/components/ScorporoDealer";
import { RicercaMobile, ScrollInfinito } from "@/components/ListaMobile";

const TESTUALI = ["codice_dealer", "ragione_sociale", "commerciale_nome", "settore", "stato"];

const COLONNE = [
  { label: "Codice", key: "codice_dealer" },
  { label: "Ragione sociale", key: "ragione_sociale" },
  { label: "Commerciale", key: "commerciale_nome" },
  { label: "Settore", key: "settore" },
  { label: "Stato", key: "stato" },
  { label: "Attivazione", key: "data_attivazione" },
  { label: "Caricate", key: "pratiche_caricate", align: "right" },
  { label: "Approvate", key: "pratiche_approvate", align: "right" },
  { label: "Importo", key: "importo_caricato", align: "right" },
  { label: "Ultima pratica", key: "ultima_pratica" },
  { label: "Giorni", key: null, align: "right" },
];

export default function Convenzionati() {
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: 2026 });
  const [search, setSearch] = useState("");
  const [stato, setStato] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [accumulate, setAccumulate] = useState([]);
  const richiesta = useRef(0);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [soloIncompleti, setSoloIncompleti] = useState(false);
  const [soloCondivisi, setSoloCondivisi] = useState(false);
  const [reload, setReload] = useState(0);
  // l'ordinamento non viene memorizzato: alla riapertura si torna alle pratiche caricate
  const [ordine, setOrdine] = useState({ key: "pratiche_caricate", dir: "desc" });
  const navigate = useNavigate();

  const ordinaPer = (key) => {
    setOrdine((o) => (o.key === key
      ? { key, dir: o.dir === "desc" ? "asc" : "desc" }
      : { key, dir: TESTUALI.includes(key) ? "asc" : "desc" }));
    setPage(1);
  };

  useEffect(() => {
    const mia = ++richiesta.current;
    const t = setTimeout(() => {
      api.get(`/dealers?${qs({ anno: filters.anno, commerciale: filters.commerciale, settore: filters.settore, stato, search, page, ordina: ordine.key, dir: ordine.dir, solo_da_completare: soloIncompleti || undefined, solo_condivisi: soloCondivisi || undefined })}`)
        .then((r) => {
          if (mia !== richiesta.current) return;
          setData(r.data);
          setAccumulate((prec) => (page === 1 ? r.data.rows : [...prec, ...r.data.rows]));
        })
        .catch(() => setData(null));
    }, 300);
    return () => clearTimeout(t);
  }, [filters, search, stato, page, reload, soloIncompleti, soloCondivisi, ordine]);

  const pages = data ? Math.ceil(data.total / data.page_size) : 1;
  // gli export rispettano filtri e ordinamento visibili a schermo
  const queryExport = qs({ anno: filters.anno, commerciale: filters.commerciale,
    settore: filters.settore, stato, search, ordina: ordine.key, dir: ordine.dir,
    solo_da_completare: soloIncompleti || undefined });
  const { T, Barra } = useTitoliPagina("convenzionati");

  return (
    <div data-testid="convenzionati-page">
      <Barra />
      <SectionTitle sub={data ? `${num(data.total)} convenzionati · produzione anno ${data.anno}` : "Caricamento…"}
        right={
          <div className="flex flex-wrap gap-2">
            <a href={`/api/export/convenzionati.pdf?${queryExport}`} target="_blank" rel="noreferrer"
              data-testid="btn-export-convenzionati-pdf"
              className="px-4 py-2 rounded-md border border-[#0018A8] text-[#0018A8] text-sm
                hover:bg-slate-100 transition-colors duration-200">
              PDF
            </a>
            <a href={`/api/export/convenzionati.xlsx?${queryExport}`}
              data-testid="btn-export-convenzionati-xlsx"
              className="px-4 py-2 rounded-md border border-emerald-600 text-emerald-700 text-sm
                hover:bg-emerald-50 transition-colors duration-200">
              Excel
            </a>
            <button type="button" onClick={() => setNuovoOpen((v) => !v)} data-testid="btn-nuovo-convenzionato"
              className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700 transition-colors duration-200">
              {nuovoOpen ? "Chiudi" : "+ Nuovo convenzionato"}
            </button>
          </div>
        }><T chiave="titolo" originale="Convenzionati" /></SectionTitle>

      {!!data?.da_completare && (
        <Card className="p-4 mb-6 border-2 border-amber-300 bg-amber-50/70" data-testid="alert-da-completare">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-amber-900">
              <b>{num(data.da_completare)} convenzionati</b> hanno l'anagrafica incompleta
              (commerciale, settore, stato, data attivazione, CAP o provincia): completala per avere
              mappa, classifiche e report corretti.
            </p>
            <button type="button" data-testid="btn-vedi-da-completare"
              onClick={() => { setSoloIncompleti((v) => !v); setPage(1); }}
              className="px-3 py-1.5 rounded-md bg-amber-600 text-white text-sm hover:bg-amber-700
                transition-colors duration-200">
              {soloIncompleti ? "Mostra tutti" : "Mostra solo da completare"}
            </button>
          </div>
        </Card>
      )}

      {!!data?.gestione_condivisa && (
        <Card className="p-4 mb-6 border-2 border-sky-300 bg-sky-50/70" data-testid="alert-gestione-condivisa">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-sky-900">
              <b>{num(data.gestione_condivisa)} convenzionati</b> hanno una gestione condivisa tra due
              commerciali (es. "FOSSI/ALFIERI"): per ora sono attribuiti al primo cognome. Apri la scheda
              e scegli il commerciale unico per chiudere la segnalazione.
            </p>
            <button type="button" data-testid="btn-vedi-condivisi"
              onClick={() => { setSoloCondivisi((v) => !v); setPage(1); }}
              className="px-3 py-1.5 rounded-md bg-sky-700 text-white text-sm hover:bg-sky-800
                transition-colors duration-200">
              {soloCondivisi ? "Mostra tutti" : "Mostra solo gestione condivisa"}
            </button>
          </div>
        </Card>
      )}

      {nuovoOpen && (
        <DealerForm meta={meta} onCancel={() => setNuovoOpen(false)}
          onSaved={() => { setReload((r) => r + 1); }} />
      )}
      <ScorporoDealer meta={meta} onDone={() => setReload((r) => r + 1)} />
      <FilterBar meta={meta} value={filters} onChange={(v) => { setFilters(v); setPage(1); }}
        fields={["anno", "commerciale", "settore"]}
        extra={
          <>
            <select className={inputCls} value={stato} onChange={(e) => { setStato(e.target.value); setPage(1); }} data-testid="filter-stato-dealer">
              <option value="">Tutti gli stati</option>
              <option value="ATTIVO">Attivo</option>
              <option value="CHIUSO">Chiuso</option>
            </select>
            <input className={inputCls} placeholder="Cerca ragione sociale o codice…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="dealer-search" />
          </>
        } />

      <RicercaMobile valore={search} testId="dealer-search-mobile"
        placeholder="Cerca ragione sociale o codice…"
        onChange={(v) => { setSearch(v); setPage(1); }} />

      <div className="sm:hidden space-y-3 mt-3" data-testid="dealers-cards">
        {accumulate.map((r) => (
          <Card key={r.codice_dealer} data-testid={`dealer-card-${r.codice_dealer}`}
            onClick={() => navigate(`/convenzionati/${r.codice_dealer}`)}
            className="p-4 active:bg-slate-50 transition-colors duration-150 cursor-pointer">
            <div className="flex items-start gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{r.ragione_sociale}</div>
                <div className="font-mono text-xs text-slate-400">{r.codice_dealer}</div>
              </div>
              <span className={`ml-auto shrink-0 text-[10px] border rounded px-2 py-0.5
                ${r.stato === "ATTIVO" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.stato}</span>
            </div>
            <div className="mt-3 flex items-end justify-between gap-2">
              <div className="text-xs text-slate-500 min-w-0">
                <div className="truncate">{r.commerciale_nome} · {r.settore}</div>
                <div>ultima pratica {dateIt(r.ultima_pratica)}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-base font-semibold">{eurShort(r.importo_caricato)}</div>
                <div className="text-[10px] font-mono text-slate-400">
                  {num(r.pratiche_caricate)} caricate · {num(r.pratiche_approvate)} approvate
                </div>
              </div>
            </div>
          </Card>
        ))}
        {data && !accumulate.length && (
          <Card className="p-8 text-center text-sm text-slate-500">Nessun convenzionato corrisponde ai filtri.</Card>
        )}
        {!!accumulate.length && (
          <ScrollInfinito attivo={page < pages} caricando={!data}
            onAltro={() => setPage((v) => (v < pages ? v + 1 : v))} />
        )}
      </div>

      <Card className="hidden sm:block overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dealers-table">
            <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              {COLONNE.map((c) => (
                <th key={c.label} data-testid={c.key ? `sort-dealer-${c.key}` : undefined}
                  onClick={() => c.key && ordinaPer(c.key)}
                  className={`px-4 py-3 text-${c.align || "left"} ${c.key
                    ? "cursor-pointer select-none hover:text-slate-900 transition-colors" : ""}`}>
                  {c.label}
                  {ordine.key === c.key ? (ordine.dir === "desc" ? " ↓" : " ↑") : ""}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {(data?.rows || []).map((r) => (
                <tr key={r.codice_dealer} data-testid={`dealer-row-${r.codice_dealer}`}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-200"
                  onClick={() => navigate(`/convenzionati/${r.codice_dealer}`)}>
                  <td className="px-4 py-3 font-mono">{r.codice_dealer}</td>
                  <td className="px-4 py-3 font-medium">
                    {r.ragione_sociale}
                    {r.da_completare && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider
                        text-amber-700 bg-amber-100 rounded px-1.5 py-0.5"
                        data-testid={`badge-da-completare-${r.codice_dealer}`}>da completare</span>
                    )}
                    {r.gestione_condivisa && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider
                        text-sky-800 bg-sky-100 rounded px-1.5 py-0.5" title={r.gestione_raw}
                        data-testid={`badge-condiviso-${r.codice_dealer}`}>doppio cognome</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.commerciale_nome}</td>
                  <td className="px-4 py-3">{r.settore}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs border rounded px-2 py-0.5 ${r.stato === "ATTIVO" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.stato}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-500">
                    {dateIt(r.data_attivazione)}
                    {r.data_chiusura && <span className="block text-red-500">chiuso il {dateIt(r.data_chiusura)}</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{num(r.pratiche_caricate)}</td>
                  <td className="px-4 py-3 text-right font-mono">{num(r.pratiche_approvate)}</td>
                  <td className="px-4 py-3 text-right font-mono">{eurShort(r.importo_caricato)}</td>
                  <td className="px-4 py-3 font-mono text-slate-500">{dateIt(r.ultima_pratica)}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.giorni_da_ultima_pratica === null ? "n/d" : num(r.giorni_da_ultima_pratica)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
          <span className="text-slate-500">Pagina {page} di {pages || 1}</span>
          <div className="flex gap-2">
            <button className="border border-slate-200 rounded px-3 py-1 disabled:opacity-40" disabled={page <= 1}
              onClick={() => setPage(page - 1)} data-testid="dealer-page-prev">Precedente</button>
            <button className="border border-slate-200 rounded px-3 py-1 disabled:opacity-40" disabled={page >= pages}
              onClick={() => setPage(page + 1)} data-testid="dealer-page-next">Successiva</button>
          </div>
        </div>
      </Card>
    </div>
  );
}
